import { useCallback, useRef, type RefObject } from "react";
import { MP_DRAG_THRESHOLD } from "./layoutConstants";

const PAN_BLOCK_SELECTOR =
  ".mp-bar, .mp-bar-resize, .mp-absence-bar, .mp-label, button, a, input, select, textarea";

const PAN_SURFACE_SELECTOR = ".mp-track, .mp-separator-track";

function canStartPanFromTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.closest(PAN_BLOCK_SELECTOR)) return false;
  return !!target.closest(PAN_SURFACE_SELECTOR);
}

interface UseScrollPanOptions {
  scrollRef: RefObject<HTMLDivElement | null>;
  enabled: boolean;
}

export function useScrollPan({ scrollRef, enabled }: UseScrollPanOptions) {
  const suppressClickRef = useRef(false);
  const sessionRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startScrollLeft: number;
  } | null>(null);
  const movedRef = useRef(false);

  const endSession = useCallback(() => {
    sessionRef.current = null;
    document.body.classList.remove("mp-panning");
  }, []);

  const onPanPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!enabled) return;
      if (event.button !== 0) return;
      if (event.pointerType === "touch") return;
      if (!canStartPanFromTarget(event.target)) return;

      const scroll = scrollRef.current;
      if (!scroll) return;

      suppressClickRef.current = false;
      movedRef.current = false;
      sessionRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startScrollLeft: scroll.scrollLeft,
      };

      const onPointerMove = (e: PointerEvent) => {
        const session = sessionRef.current;
        if (!session || e.pointerId !== session.pointerId) return;

        const dx = e.clientX - session.startX;
        const dy = e.clientY - session.startY;
        if (!movedRef.current) {
          if (Math.hypot(dx, dy) < MP_DRAG_THRESHOLD) return;
          movedRef.current = true;
          suppressClickRef.current = true;
          document.body.classList.add("mp-panning");
        }

        scroll.scrollLeft = session.startScrollLeft - dx;
        e.preventDefault();
      };

      const onPointerUp = (e: PointerEvent) => {
        const session = sessionRef.current;
        if (!session || e.pointerId !== session.pointerId) return;
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
        document.removeEventListener("pointercancel", onPointerUp);
        endSession();
      };

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
      document.addEventListener("pointercancel", onPointerUp);
    },
    [enabled, endSession, scrollRef],
  );

  return {
    onPanPointerDown,
    suppressPanClickRef: suppressClickRef,
  };
}
